import type { PromotionHighlightsFields } from "@/lib/promotion-offer-jsonld";

// Campaigns are authored by a Colorado-based team, and Contentful already stores
// this entry's end bound with a -06:00 offset. A date-only bound carries no zone,
// so it is resolved here rather than defaulting to UTC.
const CAMPAIGN_TIMEZONE = "America/Denver";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

const offsetFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: CAMPAIGN_TIMEZONE,
  timeZoneName: "longOffset",
});

/**
 * Minutes to add to a wall-clock time in the campaign timezone to reach UTC.
 * Derived per instant so daylight saving is handled (MDT -06:00 / MST -07:00)
 * instead of baked in as a constant that would be wrong half the year.
 */
function utcOffsetMinutes(at: number): number {
  const name = offsetFormatter.formatToParts(new Date(at)).find(part => part.type === "timeZoneName")?.value;
  const match = name?.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const [, sign, hours, minutes] = match;
  const total = Number(hours) * 60 + Number(minutes);
  return sign === "-" ? -total : total;
}

/** Converts a wall-clock instant in the campaign timezone to a real UTC instant. */
function fromCampaignWallClock(wallClockUtc: number): number {
  // Two passes: the offset depends on the instant, and the instant depends on the
  // offset. One correction settles it except exactly inside a DST transition.
  const firstPass = wallClockUtc - utcOffsetMinutes(wallClockUtc) * 60_000;
  return wallClockUtc - utcOffsetMinutes(firstPass) * 60_000;
}

/**
 * Resolves a bound to a UTC instant.
 *
 * A date-only value means the whole campaign day: a start opens at 00:00 local,
 * an end runs to the final millisecond of that day. Parsing "2026-10-01" as
 * midnight UTC would instead end the campaign at 18:00 on Sep 30 local — before
 * the advertised day had begun.
 *
 * Values carrying an explicit time are honoured exactly as authored.
 * Unparseable values return null so a malformed date cannot retire a campaign.
 */
function bound(value: string | undefined, edge: "start" | "end"): number | null {
  if (!value) return null;

  if (DATE_ONLY.test(value)) {
    const midnightUtc = Date.parse(`${value}T00:00:00Z`);
    if (Number.isNaN(midnightUtc)) return null;
    const dayStart = fromCampaignWallClock(midnightUtc);
    return edge === "start" ? dayStart : fromCampaignWallClock(midnightUtc + DAY_MS) - 1;
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * Whether a promotion should be advertised right now. `active` is the editor's
 * kill switch; the availability window expires the campaign on its own, with no
 * publish and no webhook — which is why callers must evaluate this per request
 * rather than caching the result.
 *
 * Both bounds are inclusive.
 */
export function isPromotionLive(fields: PromotionHighlightsFields, now: Date): boolean {
  if (!fields.active) return false;

  const at = now.getTime();
  const starts = bound(fields.availabilityStarts, "start");
  if (starts !== null && at < starts) return false;

  const ends = bound(fields.availabilityEnds, "end");
  if (ends !== null && at > ends) return false;

  return true;
}
